import { useCallback, useEffect, useRef, useState } from 'react';
import type { I18nKey } from '../i18n/index.ts';

/**
 * Tiny hook for one-shot copy-to-clipboard with visual confirmation.
 *
 * Mirrors `apps/ops-console/src/hooks/useClipboard.tsx` — both consoles
 * need the same "click to copy" affordance (API keys in console-web,
 * gift-card codes in ops-console) with the same HTTPS/insecure
 * fallback semantics.
 *
 * Returns:
 *   - `copy(text)`  — attempts the copy. On success `copied` flips to
 *     true for `resetMs` milliseconds (default 1500ms).
 *   - `copied`      — true while the visual confirmation should show.
 *   - `error`       — populated on failure; cleared on the next attempt.
 *
 * Resilience:
 *   - Production callers run inside an HTTPS origin, so
 *     `navigator.clipboard.writeText` is available.
 *   - Local dev on the Vite host + older Safari fall back to
 *     a `document.execCommand('copy')` shim against a hidden
 *     `<textarea>`, which works on insecure origins. Keeps the "click
 *     to copy" affordance functional during dev without forcing a
 *     browser switch.
 *   - Final fallback: select the text via the Selection API so the
 *     user can press Cmd/Ctrl+C themselves; `error` is still populated
 *     so the UI can prompt them.
 */

interface UseClipboardOptions {
  readonly resetMs?: number;
}

export type ClipboardErrorCode =
  | 'clipboard_denied'
  | 'clipboard_unavailable'
  | 'copy_rejected'
  | 'copy_failed';

interface UseClipboardResult {
  readonly copy: (text: string) => Promise<boolean>;
  readonly copied: boolean;
  readonly error: ClipboardErrorCode | null;
}

const DEFAULT_RESET_MS = 1500;

const CLIPBOARD_ERROR_KEYS: Record<ClipboardErrorCode, I18nKey> = {
  clipboard_denied: 'clipboard.error.denied',
  clipboard_unavailable: 'clipboard.error.unavailable',
  copy_rejected: 'clipboard.error.rejected',
  copy_failed: 'clipboard.error.failed',
};

export function clipboardErrorKey(code: ClipboardErrorCode): I18nKey {
  return CLIPBOARD_ERROR_KEYS[code];
}

export function useClipboard(options: UseClipboardOptions = {}): UseClipboardResult {
  const resetMs = options.resetMs ?? DEFAULT_RESET_MS;
  const [copied, setCopied] = useState(false);
  const [error, setFailureCode] = useState<ClipboardErrorCode | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reset = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setCopied(false);
      timerRef.current = null;
    }, resetMs);
  }, [resetMs]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      setFailureCode(null);
      const clipboard =
        typeof navigator !== 'undefined' && 'clipboard' in navigator
          ? navigator.clipboard
          : undefined;
      if (clipboard !== undefined && typeof clipboard.writeText === 'function') {
        try {
          await clipboard.writeText(text);
          setCopied(true);
          reset();
          return true;
        } catch {
          setFailureCode('clipboard_denied');
        }
      }

      if (typeof document === 'undefined') {
        setFailureCode('clipboard_unavailable');
        return false;
      }

      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', 'true');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.style.pointerEvents = 'none';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        const ok = document.execCommand('copy');
        if (ok) {
          setCopied(true);
          setFailureCode(null);
          reset();
          return true;
        }
        setFailureCode('copy_rejected');
        return false;
      } catch {
        setFailureCode('copy_failed');
        return false;
      } finally {
        document.body.removeChild(textarea);
      }
    },
    [reset],
  );

  return { copy, copied, error };
}
